import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Skeleton } from '@immo/ui-kit';
import { FLEET_HEALTH_WARN_PERCENT, type FleetHostHealth, type FleetHostHealthPoint } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { ClockIcon } from '@/components/layout/icons';
import { floorTenth, formatFactor, limitsOf, LOAD_BAR_SCALE, ratioPercent } from './health-figures';
import { HealthLineChart, type ChartPoint } from './health-line-chart';
import { useHostHealthSeries } from './use-host-health-series';

const DAY_MS = 24 * 3_600_000;

type Props = {
  readonly machineId: string;
  readonly health: FleetHostHealth | null;
};

/** The panel's last 24 hours: four charts against their warning and throttle lines (IMM-1450). */
export function Last24HoursCard({ machineId, health }: Props) {
  const t = useTranslations('pages.admin.fleet.health');
  // Recomputed when the machine changes or a reading with a new `measuredAt` lands, never on the 10-second poll alone.
  const range = useMemo(() => {
    const to = new Date();

    return { from: new Date(to.getTime() - DAY_MS), to };
  }, [machineId, health?.measuredAt]);
  const { status, series } = useHostHealthSeries(machineId, range.from, range.to);

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base-sm font-medium text-text-primary">{t('last-24h')}</h3>
        <Button size="sm" variant="ghost" asChild>
          <Link to={`/fleet/history?machine=${machineId}`}>
            <ClockIcon className="h-4 w-4" aria-hidden />
            {t('history-link')}
          </Link>
        </Button>
      </div>
      {status === 'loading' && <Skeleton className="h-52 w-full" />}
      {status === 'failed' && (
        <p className="py-10 text-center text-base-sm text-text-tertiary">{t('series-load-error')}</p>
      )}
      {status === 'ready' && series !== null && series.points.length === 0 && (
        <p className="py-10 text-center text-base-sm text-text-tertiary">{t('no-readings-24h')}</p>
      )}
      {status === 'ready' && series !== null && series.points.length > 0 && (
        <Charts
          points={series.points}
          bucketMs={series.bucketMs}
          from={range.from}
          to={range.to}
          health={series.latest ?? health}
        />
      )}
    </div>
  );
}

function Charts({
  points,
  bucketMs,
  from,
  to,
  health,
}: {
  readonly points: ReadonlyArray<FleetHostHealthPoint>;
  readonly bucketMs: number;
  readonly from: Date;
  readonly to: Date;
  readonly health: FleetHostHealth | null;
}) {
  const t = useTranslations('pages.admin.fleet.health');
  const limits = limitsOf(health);

  const series = (value: (point: FleetHostHealthPoint) => number | null): ChartPoint[] =>
    points.map((point) => ({ at: new Date(point.at), value: value(point) }));

  const percentLabel = (percent: number) => t('threshold-percent', { percent });
  const loadValues = series((point) => (point.loadPerCore === null ? null : floorTenth(point.loadPerCore)));
  const loadMax = Math.max(LOAD_BAR_SCALE, ...loadValues.map((point) => point.value ?? 0));

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
      <HealthLineChart
        title={t('disk')}
        points={series((point) => (point.diskRatio === null ? null : ratioPercent(point.diskRatio)))}
        from={from}
        to={to}
        bucketMs={bucketMs}
        yMax={100}
        threshold={{ value: FLEET_HEALTH_WARN_PERCENT }}
        formatValue={percentLabel}
      />
      <HealthLineChart
        title={t('memory')}
        points={series((point) => (point.memoryRatio === null ? null : ratioPercent(point.memoryRatio)))}
        from={from}
        to={to}
        bucketMs={bucketMs}
        yMax={100}
        threshold={{ value: FLEET_HEALTH_WARN_PERCENT }}
        formatValue={percentLabel}
      />
      <HealthLineChart
        title={t('temperature')}
        points={series((point) => (point.cpuTemperatureC === null ? null : Math.floor(point.cpuTemperatureC)))}
        from={from}
        to={to}
        bucketMs={bucketMs}
        yMax={100}
        threshold={{ value: limits.temperatureC.throttleAt }}
        formatValue={(degrees) => t('temperature-value', { degrees })}
      />
      <HealthLineChart
        title={t('load')}
        points={loadValues}
        from={from}
        to={to}
        bucketMs={bucketMs}
        yMax={loadMax}
        threshold={{ value: limits.loadPerCore.throttleAt }}
        formatValue={(factor) => t('threshold-load', { factor: formatFactor(factor) })}
      />
    </div>
  );
}
